<script lang="ts" setup>
  import { type Component, toRefs } from 'vue'

  import { EffortJsonldVueGlobalSearchDefaultReadSync } from '@/api'
  import PreviewActions from '@/components/global-search/PreviewActions.vue'
  import { BlockedIcon } from '@/config/icons'
  import { useGeneratedBusinessPartnerStore, useGeneratedEffortStateStore, useGeneratedEmployeeStore } from '@/stores'
  import type { HintedString } from '@primevue/core'
  import { iri2id } from '@/helpers/iri.ts'

  interface Props {
    data: EffortJsonldVueGlobalSearchDefaultReadSync
    omitActions?: boolean
    icon: Component
    alt: string
  }

  const props = defineProps<Props>()
  const { data, omitActions } = toRefs(props)
  const emit = defineEmits<{
    effortSelected: [EffortJsonldVueGlobalSearchDefaultReadSync]
    showOverlay: [EffortJsonldVueGlobalSearchDefaultReadSync]
    shortcutClicked: []
  }>()

  function onSelect() {
    emit('effortSelected', data.value)
  }

  const partner = computedAsync(
    async () => data.value.partner ? useGeneratedBusinessPartnerStore().find({
        query: {
          code: iri2id(data.value.partner as string),
          'properties[]': ['code', 'name', 'type']
        }
      }).then(d => d.value)
      /*useGeneratedBusinessPartnerStore().fetchByIri(data.value.partner).then((d) => d.value)*/
      : undefined
    , undefined, { lazy: true }
  )
  const effortState = computedAsync(
    async () =>
      data.value.effortState
        ? await useGeneratedEffortStateStore()
          .fetchByIri(data.value.effortState)
          .then((d) => d.value.name)
        : '',
    undefined,
    { lazy: true }
  )
  const effortOwner = computedAsync(
    async () =>
      data.value.owner && data.value.owner.employee
        ? await useGeneratedEmployeeStore()
          .fetchByIri(data.value.owner.employee)
          .then((d) => d.value.displayname)
        : 'N/A',
    undefined,
    { lazy: true }
  )
  const effortResponsible = computedAsync(
    async () =>
      data.value.responsible && data.value.responsible.employee
        ? await useGeneratedEmployeeStore()
          .fetchByIri(data.value.responsible.employee)
          .then((d) => d.value.displayname)
        : 'N/A',
    undefined,
    { lazy: true }
  )

  const severityList: Record<string, HintedString<'secondary' | 'success' | 'info' | 'warn' | 'danger' | 'contrast'> | undefined> = {
    offen: 'secondary',
    'in Bearbeitung': 'success',
    geschlossen: 'contrast'
  }

  const formatter = useFormatter()
  const isPartnerCollector = computed(() => data.value.partnerCollector)
  const statusLineSet = computed(() =>
    [
      { label: 'Belegnummer', value: data.value.externalDocReference },
      { label: 'Belegdatum', value: formatter.Date(data.value.externalDocCreationDate) },
      { label: 'Lieferdatum', value: formatter.Date(data.value.shippingDate) }
    ].filter((s) => s.value)
  )
</script>

<template>
  <div
    class="md:flex md:space-x-3 group cursor-pointer text-gray-900 dark:text-gray-300 text-sm border border-gray-300 rounded-lg py-2.5 px-4 hover:bg-gray-200 dark:hover:bg-gray-950 ring-0 focus:ring-4 focus:outline-none focus:ring-gray-100 items-center">
    <div
      class="flex space-x-3 flex-1 overflow-x-hidden"
      @click.prevent="onSelect">
      <div class="flex-shrink-0 items-center">
        <BlockedIcon
          v-if="data.blocked"
          :size="40"
          alt="Objekt ist geschlossen"
          class="h-10 w-10 rounded-full text-error" />
        <component
          :is="props.icon"
          v-else
          :alt="props.alt"
          class="h-10 w-10 rounded-full" />
      </div>
      <div class="min-w-0 flex-1">
        <!--   RESULT LINE START   -->
        <!-- title -->
        <div class="font-medium text-base">
          <div class="inline-flex gap-x-1 items-center">
            <span v-if="!isPartnerCollector">
              <template v-if="effortState">
                <Tag
                  :severity="severityList[effortState]"
                  :value="effortState"
                  class="self-center"
                  rounded />
              </template>
              <Skeleton
                v-else
                class="p-tag p-tag-rounded self-center"
                height="1.5rem"
                width="2.5rem" />
            </span>
            <span>{{ data.title }}</span>
          </div>
        </div>
        <!-- status line   -->
        <div
          v-if="!isPartnerCollector"
          class="text-xs inline-flex gap-x-2">
          <!--          <div class="separator" />-->
          <div class="inline-flex gap-x-1">
            <span>Bearbeiter:</span>
            <span>
              <template v-if="effortOwner">{{ effortOwner }}</template>
              <Skeleton
                v-else
                height="0.875rem"
                width="2rem" />
            </span>
          </div>
          <div class="inline-flex gap-x-1">
            <span>Verantwortlicher:</span>
            <span>
              <template v-if="effortResponsible">{{ effortResponsible }}</template>
              <Skeleton
                v-else
                height="0.875rem"
                width="2rem" />
            </span>
          </div>
        </div>
        <!-- partner line -->
        <template v-if="partner">
          <div
            v-if="isPartnerCollector"
            class="text-blue-500">
            Kundennummer: {{ partner.code }}
          </div>
          <div
            v-else
            class="text-blue-500 items-center flex gap-x-1">
            <span>Kunde:</span>
            <span v-if="partner">{{ partner.code }} - {{ partner.name }}</span>
            <Skeleton
              v-else
              border-radius="4px"
              class="py-1"
              height="0.875rem"
              width="8rem" />
          </div>
        </template>
        <!--     external doc ref line   -->
        <div
          v-if="!isPartnerCollector"
          class="text-xs inline-flex gap-x-2">
          <template
            v-for="(set, index) in statusLineSet"
            :key="set.label">
            <div
              v-if="set.value"
              class="inline-flex gap-x-1">
              <span>{{ set.label }}:</span>
              <span>{{ set.value }}</span>
            </div>
            <div
              v-if="index < statusLineSet.length - 1"
              class="separator" />
          </template>
          <!--          <hr />-->
          <!--          <div-->
          <!--            v-if="data.externalDocReference"-->
          <!--            class="inline-flex gap-x-1">-->
          <!--            <span>Belegnummer:</span>-->
          <!--            <span>{{ data.externalDocReference }}</span>-->
          <!--          </div>-->
          <!--          <div-->
          <!--            v-if="data.externalDocCreationDate"-->
          <!--            class="separator" />-->

          <!--          <div-->
          <!--            v-if="data.externalDocCreationDate"-->
          <!--            class="inline-flex gap-x-1">-->
          <!--            <span>Belegdatum:</span>-->
          <!--            <span>{{ $formatter.Date(data.externalDocCreationDate) }}</span>-->
          <!--          </div>-->

          <!--          <div-->
          <!--            v-if="data.shippingDate"-->
          <!--            class="separator" />-->

          <!--          <div-->
          <!--            v-if="data.shippingDate"-->
          <!--            class="inline-flex gap-x-1">-->
          <!--            <span>Lieferdatum:</span>-->
          <!--            <span>{{ $formatter.Date(data.shippingDate) }}</span>-->
          <!--          </div>-->
        </div>
        <slot />
        <!--   RESULT LINE END   -->
      </div>
    </div>
    <PreviewActions
      v-if="!omitActions"
      :effort="data"
      class="md:flex-0 md:pt-2 text-center h-full"
      @button-clicked="emit('shortcutClicked')" />
  </div>
</template>
<style scoped>
  .separator {
    @apply border border-gray-300;
  }
</style>
